import { Injectable } from '@angular/core';
import { WEBSOCKET_URL } from '../../config';

// Derive screenshot relay URL from existing chat relay URL
const SCREENSHOT_RELAY_URL = WEBSOCKET_URL.replace('chat-stream-relay', 'screenshot-relay');

@Injectable({ providedIn: 'root' })
export class ScreenshotRelayWebSocketService {
  private ws: WebSocket | null = null;
  private pingInterval: any = null;
  private pongTimeout: any = null;
  private chatId: string = '';
  private onMessageCallback: ((data: string) => void) | null = null;
  private onCloseCallback: (() => void) | null = null;

  private readonly PING_INTERVAL_MS = 20000;
  private readonly PONG_TIMEOUT_MS = 10000;

  connect(chatId: string, onMessage: (data: string) => void, onClose?: () => void) {
    this.chatId = chatId;
    this.onMessageCallback = onMessage;
    this.onCloseCallback = onClose || null;

    this.cleanup();

    console.log('[ScreenshotRelay] Connecting with chatId:', chatId);
    const wsUrl = `${SCREENSHOT_RELAY_URL}?chatId=${chatId}`;
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('[ScreenshotRelay] Connection opened');
      this.startPingInterval();
    };

    this.ws.onmessage = (event) => {
      this.clearPongTimeout();

      if (event.data === 'pong' || event.data === 'ping') {
        return;
      }
      if (this.onMessageCallback) {
        this.onMessageCallback(event.data);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[ScreenshotRelay] Error:', error);
      this.reconnect();
    };

    this.ws.onclose = (event) => {
      console.warn('[ScreenshotRelay] Connection closed. Code:', event.code);
      this.stopPingInterval();
      this.reconnect();
    };
  }

  private reconnect() {
    setTimeout(() => {
      if (this.chatId && this.onMessageCallback) {
        console.log('[ScreenshotRelay] Reconnecting...');
        this.connect(this.chatId, this.onMessageCallback, this.onCloseCallback || undefined);
      }
    }, 2000);
  }

  private startPingInterval() {
    this.stopPingInterval();

    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('ping');

        this.pongTimeout = setTimeout(() => {
          console.warn('[ScreenshotRelay] No pong received, reconnecting...');
          this.cleanup();
          this.reconnect();
        }, this.PONG_TIMEOUT_MS);
      }
    }, this.PING_INTERVAL_MS);
  }

  private stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    this.clearPongTimeout();
  }

  private clearPongTimeout() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private cleanup() {
    this.stopPingInterval();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }

  send(data: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      console.warn('[ScreenshotRelay] Cannot send - connection not open');
    }
  }

  close() {
    this.chatId = '';
    this.onMessageCallback = null;
    this.onCloseCallback = null;
    this.cleanup();
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
